/**
 * Time helpers.
 *
 * Every timestamp that crosses a boundary (D1 row, Git commit, JSON payload) is
 * an ISO-8601 UTC string, which is what SPEC §55 stores in TEXT columns.
 */

/** Matches `YYYY-MM-DDTHH:mm:ss(.sss)?Z`. */
export const ISO_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

/** Matches a bare calendar date, `YYYY-MM-DD`. */
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Current time as an ISO-8601 UTC instant. */
export function nowIso(): string {
  return new Date().toISOString();
}

/** True when `value` is an ISO-8601 UTC instant string. */
export function isIsoInstant(value: unknown): value is string {
  if (typeof value !== "string" || !ISO_INSTANT_PATTERN.test(value)) {
    return false;
  }

  return !Number.isNaN(Date.parse(value));
}

/** True when `value` is a bare `YYYY-MM-DD` calendar date. */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !ISO_DATE_PATTERN.test(value)) {
    return false;
  }

  // Reject impossible dates such as 2026-02-31, which Date.parse would accept.
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/**
 * Convert a bare calendar date to an ISO instant at midnight UTC.
 *
 * Frontmatter `date:` is a calendar date (SPEC §12) while D1 stores instants,
 * so this is the single conversion point.
 */
export function isoDateToInstant(value: string): string {
  if (!isIsoDate(value)) {
    // `isIsoDate` is a `value is string` guard, so in this branch the compiler
    // narrows `value` to `never`; `String()` keeps the message honest.
    throw new RangeError(`Not a valid ISO calendar date: ${String(value)}`);
  }

  return `${value}T00:00:00.000Z`;
}
